import * as agentScannerService from '~/services/agentScannerService';
import { selectors as commonSelectors } from '~/domains/common';
import { getActiveOrganizationId } from '~/utils/organization';
import * as actions from './actionTypes';

/**
 * Agent Scanner Actions
 */

// List Actions
export function fetchAgentScannerListRequest() {
  return {
    type: actions.FETCH_AGENT_SCANNER_LIST_REQUEST
  };
}

export function fetchAgentScannerListSuccess(pageData) {
  return {
    type: actions.FETCH_AGENT_SCANNER_LIST_SUCCESS,
    pageData
  };
}

export function fetchAgentScannerListFailure(error) {
  return {
    type: actions.FETCH_AGENT_SCANNER_LIST_FAILURE,
    error
  };
}

// Create Actions
export function createAgentScannerSuccess(scanner) {
  return {
    type: actions.CREATE_AGENT_SCANNER_SUCCESS,
    scanner
  };
}

// Update Actions
export function updateAgentScannerSuccess(scanner) {
  return {
    type: actions.UPDATE_AGENT_SCANNER_SUCCESS,
    scanner
  };
}

// Delete Actions
export function deleteAgentScannerRequest(scannerId) {
  return {
    type: actions.DELETE_AGENT_SCANNER_REQUEST,
    scannerId
  };
}

export function deleteAgentScannerSuccess(scannerId) {
  return {
    type: actions.DELETE_AGENT_SCANNER_SUCCESS,
    scannerId
  };
}

export function deleteAgentScannerFailure(scannerId, error) {
  return {
    type: actions.DELETE_AGENT_SCANNER_FAILURE,
    scannerId,
    error
  };
}

// Scanner Runs Actions
export function fetchScannerRunsRequest(scannerId) {
  return {
    type: actions.FETCH_SCANNER_RUNS_REQUEST,
    scannerId
  };
}

export function fetchScannerRunsSuccess(scannerId, pageData) {
  return {
    type: actions.FETCH_SCANNER_RUNS_SUCCESS,
    scannerId,
    pageData
  };
}

export function fetchScannerRunsFailure(scannerId, error) {
  return {
    type: actions.FETCH_SCANNER_RUNS_FAILURE,
    scannerId,
    error
  };
}

export function clearScannerRuns() {
  return {
    type: actions.CLEAR_SCANNER_RUNS
  };
}

// Agent capacity
export function fetchAgentCapacityRequest() {
  return { type: actions.FETCH_AGENT_CAPACITY_REQUEST };
}

export function fetchAgentCapacitySuccess(capacity) {
  return {
    type: actions.FETCH_AGENT_CAPACITY_SUCCESS,
    capacity
  };
}

export function fetchAgentCapacityFailure(error) {
  return {
    type: actions.FETCH_AGENT_CAPACITY_FAILURE,
    error
  };
}

export function fetchStagingAgentsRequest(scanRunId) {
  return {
    type: actions.FETCH_STAGIN_AGENTS_REQUEST,
    scanRunId
  };
}

export function fetchStagingAgentsSuccess(scanRunId, pageData) {
  return {
    type: actions.FETCH_STAGIN_AGENTS_SUCCESS,
    scanRunId,
    pageData
  };
}

export function fetchStagingAgentsFailure(scanRunId, error) {
  return {
    type: actions.FETCH_STAGIN_AGENTS_FAILURE,
    scanRunId,
    error
  };
}

export function runScanConfigRequest() {
  return {
    type: actions.RUN_SCAN_CONFIG_REQUEST
  };
}

export function abortScanConfigRequest() {
  return {
    type: actions.ABORT_SCAN_CONFIG_REQUEST
  };
}

export function abortScanConfigSuccess() {
  return {
    type: actions.ABORT_SCAN_CONFIG_SUCCESS
  };
}

export function abortScanConfigFailure(error) {
  return {
    type: actions.ABORT_SCAN_CONFIG_FAILURE,
    error
  };
}

// Thunk Actions (Async Operations)

/**
 * Load agent scanners list
 * @param {string} organizationId - Organization ID
 * @param {number} page - Page number (default: 0)
 * @param {number} size - Page size (default: 10000)
 * @returns {Function} Thunk function
 */
export function loadAgentScanners(organizationId, page = 0, size = 10000) {
  return (dispatch, getState) => {
    const state = getState();
    const context = commonSelectors.context(state);
    const profile = commonSelectors.profile(state);

    // Use getActiveOrganizationId to get the correct organizationId from profile
    const activeOrganizationId =
      getActiveOrganizationId(profile) || organizationId;

    dispatch(fetchAgentScannerListRequest());

    return agentScannerService
      .list({ organizationId: activeOrganizationId, context, page, size })
      .then((pageData) => dispatch(fetchAgentScannerListSuccess(pageData)))
      .catch((error) => {
        dispatch(fetchAgentScannerListFailure(error));
        throw error;
      });
  };
}

/**
 * Load agent capacity (used / total) for the organization.
 * On success stores { used, total }; on failure or when API is unavailable stores null (UI hides capacity line).
 *
 * @param {string} organizationId - Organization ID
 * @returns {Function}
 */
export function loadAgentCapacity(organizationId) {
  return (dispatch, getState) => {
    const state = getState();
    const context = commonSelectors.context(state);
    const profile = commonSelectors.profile(state);
    const activeOrganizationId =
      getActiveOrganizationId(profile) || organizationId;

    dispatch(fetchAgentCapacityRequest());

    return agentScannerService
      .getOrganizationInfo({
        organizationId: activeOrganizationId,
        context
      })
      .then((capacity) => dispatch(fetchAgentCapacitySuccess(capacity)))
      .catch((error) => dispatch(fetchAgentCapacityFailure(error)));
  };
}

/**
 * Builds a mapping from target system UUID to display name
 * from the connectivity/target-systems API response.
 *
 * Example input (API response):
 * [
 *   { id: 'b5e33248...', name: 'AWS Bedrock', ... },
 *   { id: '44a30473...', name: 'Google Vertex AI', ... }
 * ]
 *
 * Example output:
 * {
 *   'b5e33248-1145-465b-935f-20ac88e15fab': 'AWS Bedrock',
 *   '44a30473-605d-4ab4-b693-ac7fdaabd184': 'Google Vertex AI',
 *   'ebca1e8f-aeaa-4753-9fcb-18efd9060866': 'Microsoft Copilot Studio'
 * }
 *
 * @param {Array} targetSystems - Array of target system objects from API
 * @returns {Object} Map of targetSystemId (UUID) -> display name
 * @private
 */
function buildTargetSystemIdToNameMap(targetSystems) {
  return (targetSystems ?? []).reduce((acc, system) => {
    if (system.id && system.name) {
      acc[system.id] = system.name;
    }

    return acc;
  }, {});
}

/**
 * Enriches scanner response with target system name if missing.
 *
 * The API may return scanner objects with connection.targetSystemId but without
 * connection.targetSystem.name. This function ensures the full targetSystem object
 * exists with both id and name, enabling the UI to display "Agent Provider" correctly.
 *
 * @param {Object} scanner - Scanner object from API
 * @param {Object} targetSystemIdToNameMap - Map of target system IDs to names
 * @returns {Object} Enriched scanner object
 * @private
 */
function enrichScannerWithTargetSystemName(scanner, targetSystemIdToNameMap) {
  if (!scanner) return scanner;

  // If connection exists but targetSystem is missing or incomplete
  if (scanner.connection) {
    const targetSystemId =
      scanner.connection.targetSystemId || scanner.connection.targetSystem?.id;

    // If we have an ID but no name, add the target system object
    if (
      targetSystemId &&
      (!scanner.connection.targetSystem ||
        !scanner.connection.targetSystem.name)
    ) {
      return {
        ...scanner,
        connection: {
          ...scanner.connection,
          targetSystem: {
            id: targetSystemId,
            name: targetSystemIdToNameMap[targetSystemId] ?? targetSystemId
          }
        }
      };
    }
  }

  return scanner;
}

/**
 * Create a new agent scanner
 * @param {string} organizationId - Organization ID
 * @param {Object} scannerData - Scanner configuration data
 * @returns {Function} Thunk function
 */
export function createAgentScanner(organizationId, scannerData) {
  return (dispatch, getState) => {
    const state = getState();
    const context = commonSelectors.context(state);

    // Fetch target systems first to get the name mapping
    return agentScannerService
      .getTargetSystems({ context })
      .then((targetSystems) => {
        const targetSystemIdToNameMap =
          buildTargetSystemIdToNameMap(targetSystems);

        return agentScannerService
          .create({ organizationId, context, data: scannerData })
          .then((response) => {
            const enrichedScanner = enrichScannerWithTargetSystemName(
              response,
              targetSystemIdToNameMap
            );

            dispatch(createAgentScannerSuccess(enrichedScanner));

            return enrichedScanner;
          });
      })
      .catch((error) => {
        throw error;
      });
  };
}

/**
 * Update an existing agent scanner
 * @param {string} organizationId - Organization ID
 * @param {string} scannerId - Scanner ID
 * @param {Object} scannerData - Scanner configuration data
 * @returns {Function} Thunk function
 */
export function updateAgentScanner(organizationId, scannerId, scannerData) {
  return (dispatch, getState) => {
    const state = getState();
    const context = commonSelectors.context(state);

    // Fetch target systems first to get the name mapping
    return agentScannerService
      .getTargetSystems({ context })
      .then((targetSystems) => {
        const targetSystemIdToNameMap =
          buildTargetSystemIdToNameMap(targetSystems);

        return agentScannerService
          .update({
            organizationId,
            scannerId,
            context,
            data: scannerData
          })
          .then((response) => {
            const enrichedScanner = enrichScannerWithTargetSystemName(
              response,
              targetSystemIdToNameMap
            );

            dispatch(updateAgentScannerSuccess(enrichedScanner));

            return enrichedScanner;
          });
      })
      .catch((error) => {
        throw error;
      });
  };
}

/**
 * Load scanner runs (history) for a specific scanner
 * @param {string} organizationId - Organization ID
 * @param {string} scannerId - Scanner ID
 * @param {number} page - Page number (default: 0)
 * @param {number} size - Page size (default: 10000)
 * @returns {Function} Thunk function
 */
export function loadScannerRuns(
  organizationId,
  scannerId,
  page = 0,
  size = 10000
) {
  return (dispatch, getState) => {
    const state = getState();
    const context = commonSelectors.context(state);

    dispatch(fetchScannerRunsRequest(scannerId));

    return agentScannerService
      .listRuns({ scannerId, organizationId, context, page, size })
      .then((pageData) =>
        dispatch(fetchScannerRunsSuccess(scannerId, pageData))
      )
      .catch((error) => {
        dispatch(fetchScannerRunsFailure(scannerId, error));
        throw error;
      });
  };
}

/**
 * Get staging agents for a specific scan run
 * @param {string} organizationId - Organization ID
 * @param {string} scannerId - Scanner ID
 * @param {number} page - Page number (default: 0)
 * @param {number} size - Page size (default: 10000)
 * @returns {Function} Thunk function
 */
export function loadStagingAgents(
  organizationId,
  scannerId,
  scanRunId,
  page = 0,
  size = 10000
) {
  return (dispatch, getState) => {
    const state = getState();
    const context = commonSelectors.context(state);

    dispatch(fetchStagingAgentsRequest(scanRunId));

    return agentScannerService
      .getStagingAgents({
        scanRunId,
        scannerId,
        organizationId,
        context,
        page,
        size
      })
      .then((pageData) =>
        dispatch(fetchStagingAgentsSuccess(scanRunId, pageData))
      )
      .catch((error) => {
        dispatch(fetchStagingAgentsFailure(scanRunId, error));
        throw error;
      });
  };
}

/**
 * Run an existing scan configuration
 * @param {string} organizationId - Organization ID
 * @param {string} scannerId - Scanner ID
 * @returns {Function} Thunk function
 */
export function runScanConfiguration(organizationId, scannerId) {
  return (dispatch, getState) => {
    const state = getState();
    const context = commonSelectors.context(state);

    return agentScannerService
      .execute({
        organizationId,
        scannerId,
        context
      })
      .then((response) => {
        dispatch(runScanConfigRequest());

        return response;
      })
      .catch((error) => {
        throw error;
      });
  };
}

/**
 * Abort a running scan configuration
 * @param {string} organizationId - Organization ID
 * @param {string} scannerId - Scanner ID
 * @returns {Function} Thunk function
 */
export function abort(organizationId, scannerId) {
  return (dispatch, getState) => {
    const state = getState();
    const context = commonSelectors.context(state);

    return agentScannerService
      .abort({
        organizationId,
        scannerId,
        context
      })
      .then((response) => {
        dispatch(abortScanConfigRequest());

        return response;
      })
      .catch((error) => {
        throw error;
      });
  };
}

/**
 * Delete an agent scanner
 * @param {string} scannerId - Scanner ID to delete
 * @param {string} organizationId - Organization ID (to reload list after delete)
 * @param {boolean} deleteAgents - If true, also deletes associated agents (default: false)
 * @returns {Function} Thunk function
 */
export function deleteAgentScanner(
  scannerId,
  organizationId,
  deleteAgents = false
) {
  return (dispatch, getState) => {
    const state = getState();
    const context = commonSelectors.context(state);

    dispatch(deleteAgentScannerRequest(scannerId));

    return agentScannerService
      .deleteScanner({ organizationId, scannerId, context, deleteAgents })
      .then(() => {
        dispatch(deleteAgentScannerSuccess(scannerId));

        // Reload the list after successful deletion
        if (organizationId) {
          dispatch(loadAgentScanners(organizationId));
        }
      })
      .catch((error) => {
        dispatch(deleteAgentScannerFailure(scannerId, error));
        throw error;
      });
  };
}
